import generateChannelsRails from '@shared/utils/generic/generateChannelsRails';
import useEpg from '@shared/components/utils/hooks/useEpg';
import Category from '@shared/classes/category';
import Focus from '@shared/types/enums/focus';
import Rail from '@shared/classes/rail';
import TMain from '@components/templates/main';
import { EventMap } from '@shared/types/interfaces/event';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { routerSelector, updateStack } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import {
  RailDisplayMode,
  RailOrderSort,
  RailOrderType,
  RailType,
} from '@shared/types/enums/rail';

export default function ClassicChannel() {
  const dispatch = useAppDispatch();
  const { favouriteChannels } = useAppSelector(channelsSelector);
  const {
    params: { current: currentParams, stack },
  } = useAppSelector(routerSelector);
  const [channel, setCurrentChannel] = useState<string | undefined>(
    (currentParams?.currentRail as Rail)?.id ||
      (currentParams as { channelId: string })?.channelId
  );

  const [channelsData, setChannelsData] = useState(favouriteChannels || []);
  const [filter, setFilter] = useState<string | undefined>(
    (currentParams?.filter as Category)?.id
  );
  const [rails, setRails] = useState<Rail[]>([]);

  const initialized = useRef(
    (currentParams as { channelId: string })?.channelId ? false : true
  );
  const railsRef = useRef(rails);
  railsRef.current = rails;

  const stackRef = useRef(stack);
  stackRef.current = stack;
  const { complete, epg, onPagination } = useEpg({
    channelId: channel,
    channelsData,
    previousEpg: (stack as unknown as { epg: EventMap }[]).length
      ? (stack[0]?.epg as EventMap)
      : undefined,
  });

  const epgRef = useRef(epg);
  epgRef.current = epg;

  // Event handlers

  const onRailChange = (currentRail: Rail | null | undefined) => {
    if (currentRail?.data.length && currentRail?.data[0]?.channel?.id?.length) {
      setCurrentChannel(currentRail?.data[0]?.channel?.id);
    }
  };

  // Effect hooks

  useEffect(() => {
    return () => {
      if (stackRef.current.length) {
        const stackIndex = stackRef.current.length - 1;
        dispatch(
          updateStack({
            index: stackIndex,
            params: { ...stackRef.current[stackIndex], epg: epgRef.current },
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    if (currentParams?.previousFocus) {
      dispatch(setFocus(currentParams.previousFocus as Focus));
    } else if (complete) {
      // Uncomment this if focusing filter list is required upon page render
      // dispatch(
      //   setFocus([Focus.RAILS_LIST, parseInt(`${Focus.RAILS_SUB_LIST}${0}`)])
      // );
    }
  }, [complete]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
    } else {
      const channelData = filter
        ? favouriteChannels?.filter((ch) => {
            return ch.category === filter;
          })
        : favouriteChannels;
      if (channelData?.length && channelData?.length > 2) {
        setCurrentChannel(channelData[0].id);
      }
      setChannelsData(channelData || []);
    }
  }, [filter]);

  useEffect(() => {
    const [filterRail] = generateChannelsRails();
    const newRails: Rail[] = [];
    channelsData?.forEach((ch) => {
      for (const [key, events] of Object.entries(epg)) {
        if (events.length && events[0].channel?.id === ch.id) {
          const rail = Rail.constructorWithData(
            {
              section: {
                title: events[0].channel?.title,
                presentationType: RailDisplayMode.CHANNEL_GUIDE_PHOTO,
                name: key,
              },
            },
            events,
            RailType.GUIDE_CHANNEL
          );
          rail.sortRail(RailOrderType.DATE, RailOrderSort.ASCENDING);
          newRails.push(rail);
        }
      }
    });
    newRails.unshift(filterRail);
    setRails(newRails);
  }, [channelsData, epg]);

  return (
    <TMain
      complete={complete}
      sliders={[]}
      rails={rails}
      hasFilters={true}
      startsInMenu={true}
      onFilterChange={setFilter}
      onPagination={onPagination}
      onRailChange={onRailChange}
    />
  );
}
